// @ts-nocheck
import { useEffect, useRef, useState, useMemo } from "react";
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineStyle,
  createSeriesMarkers,
  createChart,
} from "lightweight-charts";
import type {
  CandlestickData,
  IChartApi,
  ISeriesApi,
  ISeriesMarkersPluginApi,
  MouseEventParams,
  SeriesMarker,
  Time,
  UTCTimestamp,
} from "lightweight-charts";
import type { PointerEvent as ReactPointerEvent } from "react";
import { Eye, EyeOff, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import { formatUsdCompact } from "@/lib/formatUsd";
import { candlesFromMarketCapPrints } from "@/lib/algo/pumpSimulationMath";
import type { SimCandle, SimPrint } from "@/lib/algo/pumpSimulationMath";

const BG = "rgba(0, 0, 0, 0)";
const GRID = "rgba(255, 255, 255, 0.03)";
const FG = "rgba(255, 255, 255, 0.9)";
const CROSSHAIR = "rgba(255, 255, 255, 0.4)";
const CROSSHAIR_LABEL_BG = "rgba(15, 20, 25, 0.85)";
const MAX_RENDERED_TRADE_DOTS = 700;

export type SimChartMarker = {
  id?: string;
  time: number;
  price?: number;
  priceSol?: number;
  side: "buy" | "sell";
  label: string;
};

export function SimChartPanel({
  prints,
  markers = [],
  solUsd,
  title = "Simulated prebond chart",
}: {
  prints: SimPrint[];
  markers?: SimChartMarker[];
  solUsd: number;
  title?: string;
}) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
  const markersRef = useRef<ISeriesMarkersPluginApi<Time> | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const markerLayerRef = useRef<HTMLDivElement | null>(null);
  const visibleMarkersRef = useRef<SimChartMarker[]>([]);
  const solUsdRef = useRef(solUsd);
  const tradeBubblesVisibleRef = useRef(true);
  const syncMarkerLayerRef = useRef<() => void>(() => {});

  const [chartInterval, setChartInterval] = useState<"1s" | "5s" | "15s" | "1m">("5s");
  
  const candles = useMemo(() => {
    const bucketMs = chartInterval === "1s" ? 1000 : chartInterval === "5s" ? 5000 : chartInterval === "15s" ? 15000 : 60000;
    return candlesFromMarketCapPrints(prints, bucketMs);
  }, [prints, chartInterval]);
  const markerLayerFrameRef = useRef<number | null>(null);
  const didFitRef = useRef(false);
  const userControlledViewRef = useRef(false);
  const [tradeBubblesVisible, setTradeBubblesVisible] = useState(true);
  const [colorSet, setColorSet] = useState<"neon" | "ocean" | "cyber" | "mono" | "matrix" | "sunset" | "cotton_candy">("neon");
  
  const activeColors = useMemo(() => {
    switch (colorSet) {
      case "ocean": return { up: "#00B8FF", down: "#FF5500" };
      case "cyber": return { up: "#FF00FF", down: "#00FFFF" };
      case "mono": return { up: "#FFFFFF", down: "#555555" };
      case "matrix": return { up: "#00FF41", down: "#008F11" };
      case "sunset": return { up: "#FF7B54", down: "#932B77" };
      case "cotton_candy": return { up: "#FF9CEE", down: "#A3E4D7" };
      case "neon": default: return { up: "#00FFA3", down: "#FF3358" };
    }
  }, [colorSet]);

  useEffect(() => {
    solUsdRef.current = solUsd;
    syncMarkerLayerRef.current();
  }, [solUsd]);

  useEffect(() => {
    visibleMarkersRef.current = markers;
    syncMarkerLayerRef.current();
  }, [markers]);

  useEffect(() => {
    tradeBubblesVisibleRef.current = tradeBubblesVisible;
    if (!tradeBubblesVisible) tooltipRef.current?.classList.remove("visible");
    syncMarkerLayerRef.current();
  }, [tradeBubblesVisible]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    const chart = createChart(el, {
      autoSize: true,
      layout: {
        background: { type: ColorType.Solid, color: BG },
        textColor: FG,
        fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
        fontSize: 12,
      },
      localization: {
        locale: "en-US",
        priceFormatter: (price: number) => formatUsdCompact(price),
        timeFormatter: (time: number) => {
          const t = time as number;
          if (t < 1600000000) return "";
          const s = t - 1600000000;
          const m = Math.floor(s / 60);
          const rs = s % 60;
          return `${m}:${rs.toString().padStart(2, '0')}`;
        },
      },
      grid: { vertLines: { color: GRID }, horzLines: { color: GRID } },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
      rightPriceScale: {
        autoScale: true,
        borderColor: GRID,
        scaleMargins: { top: 0.25, bottom: 0.25 },
      },
      timeScale: {
        borderColor: GRID,
        timeVisible: true,
        secondsVisible: true,
        lockVisibleTimeRangeOnResize: true,
        shiftVisibleRangeOnNewBar: false,
        fixLeftEdge: false,
        fixRightEdge: false,
        rightOffset: 14.00,
        barSpacing: 10.68,
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: CROSSHAIR,
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: CROSSHAIR_LABEL_BG,
        },
        horzLine: {
          color: CROSSHAIR,
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: CROSSHAIR_LABEL_BG,
        },
      },
    });

    const series = chart.addSeries(CandlestickSeries, {
      upColor: activeColors.up,
      downColor: activeColors.down,
      borderVisible: false,
      wickUpColor: activeColors.up,
      wickDownColor: activeColors.down,
    });

    chartRef.current = chart;
    seriesRef.current = series;
    markersRef.current = createSeriesMarkers(series, [], { autoScale: true, zOrder: "top" });

    const markerPrice = (marker: SimChartMarker) => {
      const liveSolUsd = solUsdRef.current;
      if (Number.isFinite(marker.priceSol) && liveSolUsd && liveSolUsd > 0) return marker.priceSol! * liveSolUsd;
      return marker.price;
    };

    const markerPoint = (marker: SimChartMarker) => {
      const price = markerPrice(marker);
      if (!Number.isFinite(price)) return null;
      const x = chart.timeScale().timeToCoordinate((1600000000 + Math.floor(marker.time / 1000)) as UTCTimestamp);
      const y = series.priceToCoordinate(price!);
      if (x == null || y == null) return null;
      return {
        x,
        y: y + (marker.side === "sell" ? -18 : 18),
      };
    };

    const syncMarkerLayer = () => {
      const layer = markerLayerRef.current;
      if (!layer) return;
      layer.textContent = "";
      if (!tradeBubblesVisibleRef.current) return;
      const width = el.clientWidth;
      const height = el.clientHeight;
      const visiblePoints: Array<{ marker: SimChartMarker; x: number; y: number }> = [];
      for (const marker of visibleMarkersRef.current) {
        const point = markerPoint(marker);
        if (!point) continue;
        if (point.x < -24 || point.x > width + 24 || point.y < -24 || point.y > height + 24) continue;
        visiblePoints.push({ marker, x: point.x, y: point.y });
      }
      const step = Math.max(1, Math.ceil(visiblePoints.length / MAX_RENDERED_TRADE_DOTS));
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < visiblePoints.length; index += step) {
        const point = visiblePoints[index];
        if (!point) continue;
        const dot = document.createElement("span");
        dot.className = `chart-trade-dot ${point.marker.side}`;
        dot.style.left = `${point.x}px`;
        dot.style.top = `${point.y}px`;
        dot.setAttribute("aria-label", point.marker.label);
        fragment.appendChild(dot);
      }
      layer.appendChild(fragment);
    };
    const scheduleSyncMarkerLayer = () => {
      if (markerLayerFrameRef.current != null) return;
      markerLayerFrameRef.current = window.requestAnimationFrame(() => {
        markerLayerFrameRef.current = null;
        syncMarkerLayer();
      });
    };
    syncMarkerLayerRef.current = scheduleSyncMarkerLayer;

    const updateTooltip = (point: { x: number; y: number }, hoveredObjectId?: unknown) => {
      const tooltip = tooltipRef.current;
      if (!tooltip) {
        return;
      }
      if (!tradeBubblesVisibleRef.current) {
        tooltip.classList.remove("visible");
        return;
      }
      const hoveredMarker =
        typeof hoveredObjectId === "string"
          ? visibleMarkersRef.current.find((marker) => marker.id === hoveredObjectId)
          : null;
      let closest: { marker: SimChartMarker; distance: number } | null =
        hoveredMarker ? { marker: hoveredMarker, distance: 0 } : null;
      for (const marker of visibleMarkersRef.current) {
        const markerCoords = markerPoint(marker);
        if (!markerCoords) continue;
        const dx = markerCoords.x - point.x;
        const dy = markerCoords.y - point.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        if (distance <= 70 && (!closest || distance < closest.distance)) {
          closest = { marker, distance };
        }
      }
      if (!closest) {
        tooltip.classList.remove("visible");
        return;
      }
      const marker = closest.marker;
      tooltip.textContent = marker.label;
      tooltip.className = `chart-marker-tooltip visible ${marker.side}`;
      tooltip.style.left = `${Math.min(point.x + 14, Math.max(0, el.clientWidth - 190))}px`;
      tooltip.style.top = `${Math.max(8, point.y - 28)}px`;
    };

    const hideTooltip = () => {
      const tooltip = tooltipRef.current;
      if (tooltip) tooltip.classList.remove("visible");
    };

    const handleCrosshairMove = (param: MouseEventParams<Time>) => {
      if (!param.point) {
        hideTooltip();
        return;
      }
      updateTooltip(param.point, (param as MouseEventParams<Time> & { hoveredObjectId?: unknown }).hoveredObjectId);
    };

    const handlePointerMove = (event: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      updateTooltip({ x: event.clientX - rect.left, y: event.clientY - rect.top });
    };

    const handlePointerLeave = () => {
      hideTooltip();
    };
    const markUserControlled = () => {
      userControlledViewRef.current = true;
      scheduleSyncMarkerLayer();
    };
    const timeScaleWithEvents = chart.timeScale() as unknown as {
      subscribeVisibleLogicalRangeChange?: (handler: () => void) => void;
      unsubscribeVisibleLogicalRangeChange?: (handler: () => void) => void;
    };
    const resizeObserver = new ResizeObserver(scheduleSyncMarkerLayer);

    chart.subscribeCrosshairMove(handleCrosshairMove);
    timeScaleWithEvents.subscribeVisibleLogicalRangeChange?.(scheduleSyncMarkerLayer);
    resizeObserver.observe(el);
    el.addEventListener("pointermove", handlePointerMove);
    el.addEventListener("pointerleave", handlePointerLeave);
    el.addEventListener("pointerdown", markUserControlled);
    el.addEventListener("wheel", markUserControlled, { passive: true });
    el.addEventListener("touchstart", markUserControlled, { passive: true });
    scheduleSyncMarkerLayer();

    const handleLogicalRange = (r: any) => {
      if (!r) return;
      const dbg = document.getElementById("chart-debug-info");
      if (dbg) {
        dbg.innerText = `logical: ${r.from?.toFixed(2)} - ${r.to?.toFixed(2)} | barSpacing: ${chart.timeScale().options().barSpacing?.toFixed(2)} | rightOffset: ${chart.timeScale().options().rightOffset?.toFixed(2)}`;
      }
    };
    chart.timeScale().subscribeVisibleLogicalRangeChange(handleLogicalRange);

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(handleLogicalRange);
      chart.unsubscribeCrosshairMove(handleCrosshairMove);
      timeScaleWithEvents.unsubscribeVisibleLogicalRangeChange?.(scheduleSyncMarkerLayer);
      resizeObserver.disconnect();
      el.removeEventListener("pointermove", handlePointerMove);
      el.removeEventListener("pointerleave", handlePointerLeave);
      el.removeEventListener("pointerdown", markUserControlled);
      el.removeEventListener("wheel", markUserControlled);
      el.removeEventListener("touchstart", markUserControlled);
      chart.remove();
      if (markerLayerFrameRef.current != null) {
        window.cancelAnimationFrame(markerLayerFrameRef.current);
        markerLayerFrameRef.current = null;
      }
      chartRef.current = null;
      seriesRef.current = null;
      markersRef.current = null;
      syncMarkerLayerRef.current = () => {};
      didFitRef.current = false;
      userControlledViewRef.current = false;
    };
  }, []);

  useEffect(() => {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;

    const toUsd = (solValue: number | undefined, fallbackUsd: number) =>
      solValue != null && Number.isFinite(solValue) && solUsd && solUsd > 0
        ? solValue * solUsd
        : fallbackUsd;

    const rows: CandlestickData<UTCTimestamp>[] = candles.map((c) => ({
      time: (1600000000 + Math.floor(c.time / 1000)) as UTCTimestamp,
      open: toUsd(c.openSol, c.open),
      high: toUsd(c.highSol, c.high),
      low: toUsd(c.lowSol, c.low),
      close: toUsd(c.closeSol, c.close),
    }));

    if (rows.length === 0) {
      series.setData([]);
      markersRef.current?.setMarkers([]);
      didFitRef.current = false;
      return;
    }

    series.setData(rows);
    syncMarkerLayerRef.current();
    if (!didFitRef.current || !userControlledViewRef.current) {
      chart.timeScale().setVisibleLogicalRange({ 
        from: -17.04, 
        to: 35.70 
      });

      didFitRef.current = true;
      syncMarkerLayerRef.current();
    }
  }, [candles, solUsd]);

  useEffect(() => {
    if (seriesRef.current) {
      seriesRef.current.applyOptions({
        upColor: activeColors.up,
        downColor: activeColors.down,
        wickUpColor: activeColors.up,
        wickDownColor: activeColors.down,
      });
    }
  }, [activeColors]);

  useEffect(() => {
    const plugin = markersRef.current;
    if (!plugin) return;
    const chartMarkers: SeriesMarker<UTCTimestamp>[] = [];
    plugin.setMarkers(chartMarkers);
    syncMarkerLayerRef.current();
  }, [markers]);

  function fitView() {
    const chart = chartRef.current;
    const series = seriesRef.current;
    if (!chart || !series) return;
    userControlledViewRef.current = false;
    series.priceScale().applyOptions({ autoScale: true });
    chart.timeScale().fitContent();
  }

  function zoom(multiplier: number) {
    const chart = chartRef.current;
    if (!chart) return;
    userControlledViewRef.current = true;
    const range = chart.timeScale().getVisibleLogicalRange();
    if (!range) return;
    const center = (range.from + range.to) / 2;
    const half = ((range.to - range.from) * multiplier) / 2;
    chart.timeScale().setVisibleLogicalRange({ from: center - half, to: center + half });
  }

  return (
    <div className="chart-panel flex-1 flex flex-col min-h-0 w-full h-full">
      <div className="chart-toolbar shrink-0">
        <div className="loaded-token">
          <span className="token-dot" />
          <span>{title}</span>
          <div className="flex gap-1 ml-4 bg-black/20 p-0.5 rounded-lg border border-[var(--line)]">
            {(["1s", "5s", "15s", "1m"] as const).map((iv) => (
              <button
                key={iv}
                onClick={() => setChartInterval(iv)}
                className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase ${chartInterval === iv ? "bg-[var(--line-strong)] text-[var(--gold)]" : "text-[var(--muted)] hover:text-[var(--text)]"}`}
              >
                {iv}
              </button>
            ))}
          </div>
        </div>
        <div className="chart-nav-actions flex items-center gap-2">
          <select 
            className="field h-6 px-2 pr-6 w-28 text-[10px] uppercase font-bold" 
            value={colorSet} 
            onChange={(e) => setColorSet(e.target.value as any)}
          >
            <option value="neon">Neon</option>
            <option value="ocean">Ocean</option>
            <option value="cyber">Cyber</option>
            <option value="matrix">Matrix</option>
            <option value="sunset">Sunset</option>
            <option value="cotton_candy">Cotton Candy</option>
            <option value="mono">Mono</option>
          </select>
          <button
            type="button"
            className={`px-2 py-0.5 text-[10px] font-bold rounded-md uppercase border ${tradeBubblesVisible ? "bg-[var(--line-strong)] text-[var(--gold)] border-[var(--gold)]" : "text-[var(--muted)] hover:text-[var(--text)] border-transparent"}`}
            title={tradeBubblesVisible ? "Hide trade bubbles" : "Show trade bubbles"}
            aria-label={tradeBubblesVisible ? "Hide trade bubbles" : "Show trade bubbles"}
            onClick={() => setTradeBubblesVisible((visible) => !visible)}
          >
            Bubbles
          </button>
          <button type="button" className="btn btn-ghost p-1" title="Zoom in" aria-label="Zoom in" onClick={() => zoom(0.72)}>
            <ZoomIn size={15} />
          </button>
          <button type="button" className="btn btn-ghost p-1" title="Zoom out" aria-label="Zoom out" onClick={() => zoom(1.38)}>
            <ZoomOut size={15} />
          </button>
          <button type="button" className="btn btn-ghost p-1" title="Fit view" aria-label="Fit view" onClick={fitView}>
            <Maximize2 size={15} />
          </button>
          <div className="axis-pill ml-2">{solUsd ? `Market cap USD @ $${solUsd.toFixed(2)}/SOL` : "Market cap USD"}</div>
        </div>
      </div>
      <div ref={hostRef} className="chart-canvas flex-1 min-h-0 w-full relative">
        <div ref={markerLayerRef} className="chart-marker-layer" />
        <div ref={tooltipRef} className="chart-marker-tooltip" />
        <div 
          id="chart-debug-info" 
          className="absolute top-2 left-2 z-10 pointer-events-none text-[10px] font-mono text-white/50 bg-black/40 px-2 py-1 rounded"
        >
          logical: --
        </div>
      </div>
    </div>
  );
}
